export type PulseState = "firstTime" | "available" | "cooldown";

export interface DerivedPulseState {
  state: PulseState;
  /** Current day of cooldown (1-based). 0 when state is 'firstTime'. */
  currentDay: number;
  /** Total days in cooldown period. 0 when state is 'firstTime'. */
  totalDays: number;
  /** ISO string of nextAvailableAt. Empty string when state is 'firstTime'. */
  nextPulseDate: string;
}

/**
 * Derives Pulse UI state from the backend's two timestamps.
 *
 * The backend returns lastCompletedAt and nextAvailableAt (null-together invariant).
 * This function + the client's current time produces everything the UI components need.
 *
 * @param lastCompletedAt - ISO string or null (from pulseAvailability query)
 * @param nextAvailableAt - ISO string or null (from pulseAvailability query)
 * @param now - injectable Date for testability (defaults to new Date())
 */
export function derivePulseState(
  lastCompletedAt: string | null,
  nextAvailableAt: string | null,
  now: Date = new Date()
): DerivedPulseState {
  // Null-together invariant: both null = first time
  if (!lastCompletedAt || !nextAvailableAt) {
    return {
      state: "firstTime",
      currentDay: 0,
      totalDays: 0,
      nextPulseDate: ""
    };
  }

  const last = new Date(lastCompletedAt);
  const next = new Date(nextAvailableAt);

  // Total cooldown period in days
  const totalMs = next.getTime() - last.getTime();
  const msPerDay = 24 * 60 * 60 * 1000;
  const totalDays = Math.round(totalMs / msPerDay);

  // State: cooldown if we haven't reached nextAvailableAt yet
  const state: PulseState = now >= next ? "available" : "cooldown";

  // Days remaining until nextAvailableAt (wall-clock, always ≥ 0).
  // Using Math.ceil so that any fraction of a day still counts as 1 day —
  // e.g. 23 h 59 m left = 1 day, not 0.
  const msRemaining = Math.max(next.getTime() - now.getTime(), 0);
  const daysRemaining = Math.ceil(msRemaining / msPerDay);

  // currentDay: how far through the cooldown we are (1-based).
  // Derived from daysRemaining so it stays consistent with the display.
  const currentDay = totalDays - daysRemaining;

  return { state, currentDay, totalDays, nextPulseDate: nextAvailableAt };
}
